import React from 'react';
import { usePublicClient } from 'wagmi';
import { SoundClient } from '@soundxyz/sdk';
import { SoundAPI } from '@soundxyz/sdk/api';
import { chainIds } from '@soundxyz/utils/const';
import { useLatestRef } from '@components-shared/src/hooks/useLatestRef';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { useConnectedAuth } from '@context/auth/connected';
import { AVAILABLE_CHAIN_IDS } from '@utils/constants';
import { CLIENT_API_KEY, GATEWAY_API_URL } from '@utils/env';

export const sdkSoundAPI = SoundAPI({
  apiEndpoint: GATEWAY_API_URL,
  apiKey: CLIENT_API_KEY,
});

export function useSoundSdk() {
  const { getSigner } = useConnectedAuth();

  const ethProvider = usePublicClient({ chainId: AVAILABLE_CHAIN_IDS.eth });
  const optimismProvider = usePublicClient({ chainId: AVAILABLE_CHAIN_IDS.optimism });

  const latestEthProvider = useLatestRef(ethProvider);
  const latestOptimismProvider = useLatestRef(optimismProvider);

  const soundClientEthProvider = React.useMemo(() => {
    return SoundClient({
      client() {
        return latestEthProvider.current;
      },
      soundAPI: sdkSoundAPI,
      merkleProvider: sdkSoundAPI,
    });
  }, [latestEthProvider]);

  const soundClientOptimismProvider = React.useMemo(() => {
    return SoundClient({
      client() {
        return latestOptimismProvider.current;
      },
      soundAPI: sdkSoundAPI,
      merkleProvider: sdkSoundAPI,
    });
  }, [latestOptimismProvider]);

  const soundClientSigner = useStableCallback((chainId: number) => {
    switch (chainId) {
      case chainIds.GOERLI:
      case chainIds.MAINNET:
        return SoundClient({
          client: ethProvider,
          soundAPI: sdkSoundAPI,
          merkleProvider: sdkSoundAPI,
          account: () => getSigner(chainId),
        });
      case chainIds.OPTIMISM_GOERLI:
      case chainIds.OPTIMISM:
        return SoundClient({
          client: optimismProvider,
          soundAPI: sdkSoundAPI,
          merkleProvider: sdkSoundAPI,
          account: () => getSigner(chainId),
        });
      default:
        throw new Error(`Invalid chainId ${chainId}`);
    }
  });

  const soundClientProvider = useStableCallback((chainId: number) => {
    switch (chainId) {
      case chainIds.GOERLI:
      case chainIds.MAINNET:
        return soundClientEthProvider;
      case chainIds.OPTIMISM_GOERLI:
      case chainIds.OPTIMISM:
        return soundClientOptimismProvider;
      default:
        throw new Error(`Invalid chainId ${chainId}`);
    }
  });

  return {
    soundClientSigner,
    soundClientProvider,
  };
}
